import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

/** Shared emerald → cyan gradient used by tour cards and weather panels. */
export const TOUR_GRADIENT = 'bg-gradient-to-r from-emerald-500 to-cyan-500';

export const TOUR_PANEL_SHELL =
  'pointer-events-auto overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl backdrop-blur-xl dark:border-gray-700/60 dark:bg-gray-900 dark:shadow-black/40';

export const TOUR_PANEL_HEADER_BTN =
  'flex h-7 shrink-0 items-center gap-1 rounded-lg bg-white/15 px-2 text-[11px] font-medium text-white transition-colors hover:bg-white/25 sm:px-2.5 sm:text-xs';

export const TOUR_SEGMENTED_TRACK =
  'inline-flex items-center rounded-xl border border-gray-200/80 bg-gray-100/90 p-0.5 dark:border-gray-600 dark:bg-gray-800/90';

export function tourSegmentedBtn(active: boolean): string {
  return active
    ? 'rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 px-2.5 py-1.5 text-[11px] font-semibold leading-none text-white shadow-sm sm:text-xs'
    : 'rounded-lg px-2.5 py-1.5 text-[11px] font-semibold leading-none text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 sm:text-xs';
}

interface TourStylePanelShellProps {
  headerIcon: ReactNode;
  headerLabel: string;
  subtitle?: string | null;
  onCollapse?: () => void;
  collapseLabel?: string;
  headerActions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/** Bottom floating panel — product-tour gradient header + card body. */
export function TourStylePanelShell({
  headerIcon,
  headerLabel,
  subtitle,
  onCollapse,
  collapseLabel = 'Minimise panel',
  headerActions,
  children,
  footer,
  className = '',
}: TourStylePanelShellProps) {
  return (
    <div className={`pointer-events-auto ${TOUR_PANEL_SHELL} ${className}`}>
      <div className={`${TOUR_GRADIENT} px-3 py-2.5 sm:px-4`}>
        <div className="flex items-start gap-2">
          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              className="mt-0.5 shrink-0 rounded-lg p-0.5 text-white/75 transition-colors hover:bg-white/15 hover:text-white"
              aria-label={collapseLabel}
              title={collapseLabel}
            >
              <ChevronDown size={16} />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-white">{headerIcon}</span>
              <span className="truncate text-sm font-semibold text-white">{headerLabel}</span>
            </div>
            {subtitle && (
              <p className="mt-0.5 truncate text-xs text-white/80">{subtitle}</p>
            )}
          </div>
          {headerActions && (
            <div className="flex flex-wrap items-center justify-end gap-1.5 sm:shrink-0">
              {headerActions}
            </div>
          )}
        </div>
      </div>

      <div>{children}</div>

      {footer}
    </div>
  );
}

interface TourStylePanelMiniHeaderProps {
  headerIcon: ReactNode;
  headerLabel: string;
  subtitle?: string | null;
  trailing?: ReactNode;
}

/** Compact gradient strip for collapsed panel state. */
export function TourStylePanelMiniHeader({
  headerIcon,
  headerLabel,
  subtitle,
  trailing,
}: TourStylePanelMiniHeaderProps) {
  return (
    <div className={`${TOUR_GRADIENT} flex items-center gap-2 px-3 py-2`}>
      <span className="shrink-0 text-white">{headerIcon}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-white">{headerLabel}</p>
        {subtitle && <p className="truncate text-[10px] text-white/75">{subtitle}</p>}
      </div>
      {trailing}
    </div>
  );
}
